import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { avatarColor } from '../../lib/avatars';
import './DetailPane.css';

function isRestrictionActive(until) {
  return Boolean(until && new Date(until) > new Date());
}

function formatMemberSince(iso) {
  if (!iso) return 'Unknown';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Unknown';
  return d.toLocaleDateString([], { month: '2-digit', day: '2-digit', year: 'numeric' });
}

export default function DetailPane({
  currentView,
  // Room members props
  selectedChat,
  selectedChatAccessRoot,
  isRoomOwner,
  roomMembers,
  onlineRoomMembers,
  offlineRoomMembers,
  inviteUsername,
  inviteStatus,
  onInviteUsernameChange,
  onInvite,
  selectedRoomVoicePresence,
  isSelectedRoomVoiceLocked,
  voiceLockBusy,
  voiceModerationError,
  selectedRoomVoiceParticipantsByUserId,
  onKickTarget,
  onVoiceModerationTarget,
  onVoiceLockToggle,
  user,
  // DM props
  selectedFriend,
  dmDisplayProfile,
  dmDisplayName,
  dmMutualChats,
  dmDetailsLoading,
  isSelectedDmMuted,
  dmMenuOpen,
  onDmMenuOpen,
  onUnmuteDm,
  onOpenDmMuteModal,
}) {
  const navigate = useNavigate();
  const dmMenuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dmMenuRef.current && !dmMenuRef.current.contains(e.target)) {
        onDmMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onDmMenuOpen]);

  if (currentView === 'chats' && selectedChat && selectedChatAccessRoot) {
    return (
      <aside className="members-panel" aria-label="Members">
        <div className="members-panel-header">
          <span className="members-panel-title">Members</span>
          <span className="members-panel-count">{roomMembers.length}</span>
        </div>

        {selectedRoomVoicePresence.count > 0 && (
          <div className="members-voice-live">
            <div className="members-voice-live-title">Active In Voice</div>
            <div className="members-voice-live-copy">
              {selectedRoomVoicePresence.speakingCount > 0
                ? `${selectedRoomVoicePresence.speakingCount} speaking now`
                : `${selectedRoomVoicePresence.count} listening live`}
            </div>
          </div>
        )}

        {isRoomOwner && (
          <div className="members-voice-lock">
            <div className="members-voice-lock-title">Room Voice</div>
            {voiceModerationError && (
              <p className="members-invite-status err">{voiceModerationError}</p>
            )}
            <button
              className={`dash-btn ${isSelectedRoomVoiceLocked ? 'dash-btn-danger' : 'dash-btn-secondary'}`}
              type="button"
              disabled={voiceLockBusy}
              onClick={() => onVoiceLockToggle(!isSelectedRoomVoiceLocked)}
            >
              {voiceLockBusy
                ? 'Saving…'
                : isSelectedRoomVoiceLocked
                  ? 'Unlock Voice'
                  : 'Lock Voice'}
            </button>
          </div>
        )}

        {isRoomOwner && (
          <div className="members-invite-section">
            <div className="members-invite-row">
              <input
                className="members-invite-input"
                value={inviteUsername}
                onChange={e => onInviteUsernameChange(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && onInvite()}
                placeholder="Invite by username…"
              />
              <button className="members-invite-btn" onClick={onInvite} title="Send invite">
                +
              </button>
            </div>
            {inviteStatus && (
              <p className={`members-invite-status${inviteStatus.includes('sent') ? ' ok' : ' err'}`}>
                {inviteStatus}
              </p>
            )}
          </div>
        )}

        <div className="members-list">
          {onlineRoomMembers.length > 0 && (
            <div className="members-list-group">
              <div className="members-list-heading">Online</div>
              {onlineRoomMembers.map(m => {
                const p = m.profiles;
                const name = p?.display_name || p?.username || 'Unknown';
                const isMe = m.user_id === user.id;
                const isMemberOwner = m.role === 'owner';
                const isVoiceBanned = Boolean(m.voice_banned);
                const isVoiceRemoved = isRestrictionActive(m.voice_kicked_until);
                const isVoiceMuted = isRestrictionActive(m.voice_muted_until);
                const liveVoiceParticipant = selectedRoomVoiceParticipantsByUserId.get(m.user_id);
                const isInVoice = Boolean(liveVoiceParticipant);
                return (
                  <div key={m.user_id} className="member-row">
                    <div className="member-avatar" style={{ background: avatarColor(m.user_id) }}>
                      {name.charAt(0).toUpperCase()}
                    </div>
                    <div className="member-info">
                      <div className="member-name-row">
                        <span className="member-name">{name}{isMe ? ' (you)' : ''}</span>
                        <span className="member-presence-dot nav-friend-status" title="Online" aria-label="Online" />
                      </div>
                      {isMemberOwner && <span className="member-owner-badge">owner</span>}
                      {!isMemberOwner && (isVoiceBanned || isVoiceRemoved || isVoiceMuted) && (
                        <div className="member-voice-state">
                          {isVoiceBanned && <span className="member-voice-badge is-danger">voice banned</span>}
                          {isVoiceRemoved && <span className="member-voice-badge is-warning">voice removed</span>}
                          {isVoiceMuted && <span className="member-voice-badge">voice muted</span>}
                        </div>
                      )}
                      {isInVoice && (
                        <div className="member-voice-state">
                          <span className={`member-voice-badge${liveVoiceParticipant.isSpeaking ? ' is-live' : ''}`}>
                            {liveVoiceParticipant.isSpeaking ? 'speaking live' : 'in voice'}
                          </span>
                          {!liveVoiceParticipant.hasMicrophone && (
                            <span className="member-voice-badge">listening</span>
                          )}
                        </div>
                      )}
                    </div>
                    {isRoomOwner && !isMe && !isMemberOwner && (
                      <div className="member-actions">
                        <button
                          className="member-voice-btn"
                          onClick={() => onVoiceModerationTarget(m)}
                          title="Voice moderation"
                        >
                          🎙
                        </button>
                        <button
                          className="member-kick-btn"
                          onClick={() => onKickTarget(m.user_id)}
                          title="Kick / ban"
                        >
                          ⊘
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {offlineRoomMembers.length > 0 && (
            <div className="members-list-group">
              <div className="members-list-heading">Offline</div>
              {offlineRoomMembers.map(m => {
                const p = m.profiles;
                const name = p?.display_name || p?.username || 'Unknown';
                const isMe = m.user_id === user.id;
                const isMemberOwner = m.role === 'owner';
                const isVoiceBanned = Boolean(m.voice_banned);
                const isVoiceRemoved = isRestrictionActive(m.voice_kicked_until);
                const isVoiceMuted = isRestrictionActive(m.voice_muted_until);
                const liveVoiceParticipant = selectedRoomVoiceParticipantsByUserId.get(m.user_id);
                const isInVoice = Boolean(liveVoiceParticipant);
                return (
                  <div key={m.user_id} className="member-row">
                    <div className="member-avatar" style={{ background: avatarColor(m.user_id) }}>
                      {name.charAt(0).toUpperCase()}
                    </div>
                    <div className="member-info">
                      <div className="member-name-row">
                        <span className="member-name">{name}{isMe ? ' (you)' : ''}</span>
                      </div>
                      {isMemberOwner && <span className="member-owner-badge">owner</span>}
                      {!isMemberOwner && (isVoiceBanned || isVoiceRemoved || isVoiceMuted) && (
                        <div className="member-voice-state">
                          {isVoiceBanned && <span className="member-voice-badge is-danger">voice banned</span>}
                          {isVoiceRemoved && <span className="member-voice-badge is-warning">voice removed</span>}
                          {isVoiceMuted && <span className="member-voice-badge">voice muted</span>}
                        </div>
                      )}
                      {isInVoice && (
                        <div className="member-voice-state">
                          <span className={`member-voice-badge${liveVoiceParticipant.isSpeaking ? ' is-live' : ''}`}>
                            {liveVoiceParticipant.isSpeaking ? 'speaking live' : 'in voice'}
                          </span>
                          {!liveVoiceParticipant.hasMicrophone && (
                            <span className="member-voice-badge">listening</span>
                          )}
                        </div>
                      )}
                    </div>
                    {isRoomOwner && !isMe && !isMemberOwner && (
                      <div className="member-actions">
                        <button
                          className="member-voice-btn"
                          onClick={() => onVoiceModerationTarget(m)}
                          title="Voice moderation"
                        >
                          🎙
                        </button>
                        <button
                          className="member-kick-btn"
                          onClick={() => onKickTarget(m.user_id)}
                          title="Kick / ban"
                        >
                          ⊘
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </aside>
    );
  }

  if (currentView === 'dm' && selectedFriend) {
    return (
      <aside className="members-panel dm-details-panel" aria-label="DM details">
        <div className="dm-details-topbar">
          <div className="dm-details-menu" ref={dmMenuRef}>
            <button
              className="dm-details-menu-btn"
              type="button"
              title="More options"
              aria-label="More options"
              onClick={() => onDmMenuOpen(!dmMenuOpen)}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="3" cy="8" r="1.25" fill="currentColor" />
                <circle cx="8" cy="8" r="1.25" fill="currentColor" />
                <circle cx="13" cy="8" r="1.25" fill="currentColor" />
              </svg>
            </button>

            {dmMenuOpen && (
              <div className="detail-menu-dropdown">
                <button
                  className="detail-menu-item"
                  onClick={() => {
                    onDmMenuOpen(false);
                    if (isSelectedDmMuted) {
                      onUnmuteDm();
                    } else {
                      onOpenDmMuteModal();
                    }
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
                    {isSelectedDmMuted ? (
                      <>
                        <path d="M7 2.25a3.25 3.25 0 0 1 3.25 3.25v1.5c0 .52.19 1.02.55 1.4l.45.48H2.75l.45-.48c.36-.38.55-.88.55-1.4V5.5A3.25 3.25 0 0 1 7 2.25Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
                        <path d="M5.5 11a1.5 1.5 0 0 0 3 0" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                      </>
                    ) : (
                      <>
                        <path d="M7 2.25a3.25 3.25 0 0 1 3.25 3.25v1.5c0 .52.19 1.02.55 1.4l.45.48H2.75l.45-.48c.36-.38.55-.88.55-1.4V5.5A3.25 3.25 0 0 1 7 2.25Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
                        <path d="M5.5 11a1.5 1.5 0 0 0 3 0" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                        <path d="M2 2l10 10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                      </>
                    )}
                  </svg>
                  {isSelectedDmMuted ? 'Unmute notifications' : 'Mute notifications'}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="dm-details-profile">
          <div
            className="dm-details-avatar"
            style={{ background: avatarColor(dmDisplayProfile?.id || selectedFriend.id) }}
          >
            {dmDisplayName.charAt(0).toUpperCase()}
          </div>
          <div className="dm-details-name-row">
            <span className="dm-details-name">{dmDisplayName}</span>
          </div>
          {dmDisplayProfile?.username && (
            <span className="dm-details-handle">@{dmDisplayProfile.username}</span>
          )}
        </div>

        <div className="dm-details-section">
          <span className="dm-details-section-label">Member Since</span>
          <div className="dm-details-card">
            <span className="dm-details-meta">{formatMemberSince(dmDisplayProfile?.created_at)}</span>
          </div>
        </div>

        <div className="dm-details-section dm-details-section-fill">
          <div className="dm-details-section-head">
            <span className="dm-details-section-label">Mutual Chats</span>
            <span className="members-panel-count">{dmMutualChats.length}</span>
          </div>

          <div className="dm-details-chat-list">
            {dmDetailsLoading ? (
              <div className="dm-details-empty">Loading shared chats…</div>
            ) : dmMutualChats.length === 0 ? (
              <div className="dm-details-empty">No shared chats yet.</div>
            ) : (
              dmMutualChats.map((chat) => (
                <button
                  key={chat.id}
                  type="button"
                  className="dm-details-chat-item"
                  onClick={() => navigate(chat.parent_chat_id ? `/channel/${chat.parent_chat_id}/${chat.id}` : `/channel/${chat.id}`)}
                >
                  <span className="dm-details-chat-name">{chat.name}</span>
                  <span className="dm-details-chat-arrow" aria-hidden="true">›</span>
                </button>
              ))
            )}
          </div>
        </div>
      </aside>
    );
  }

  return null;
}
